import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createBrowserRouter, Link, useLocation, type RouteObject } from 'react-router';

import { GoTo } from '@/features/auth/destination';
import { RequireAnonymous, RequireAuth, RequireRole, ToHome } from '@/features/auth/RequireAuth';
import {
  ForgotPasswordPage,
  LoginPage,
  NotFoundPage,
  RegisterPage,
  ResetPasswordPage,
  VerifyEmailPage,
} from '@/pages/AuthPages';
import { CourseMapPage } from '@/pages/CourseMapPage';
import { HubPage } from '@/pages/HubPage';
import { ACCOUNT_MANAGERS, DEVELOPERS, LEARNERS, MEMBERS, MODERATORS, SEO_MANAGERS, STUDENTS } from '@/shared/lib/roles';
import { browser, partHere, urlOf, type Part } from '@/shared/lib/site';

import { AppShell } from './AppShell';
import { RouteCrash } from './CrashPage';

// The lesson and settings pages pull in the exercise engine and the Markdown
// renderer. They load on first visit, so signing in and the course map stay light.
const lessonPages = () => import('@/pages/LessonPages');
const settingsPage = () => import('@/pages/SettingsPage');
// The studio is used by teachers only; nobody else downloads it.
const studioPages = () => import('@/pages/StudioPages');
const accessPage = () => import('@/pages/AccessPage');
const seoPage = () => import('@/pages/SeoPage');
const moderationPage = () => import('@/pages/ModerationPage');
const teachingPages = () => import('@/pages/TeachingPages');
const historyPages = () => import('@/pages/HistoryPages');
const glossaryPage = () => import('@/pages/GlossaryPage');
const helpPages = () => import('@/pages/HelpPages');
const portalPage = () => import('@/pages/PortalPage');
const feedbackPages = () => import('@/pages/FeedbackPages');

/**
 * The pages of each part of the installation (shared/lib/site.ts). On hosts of its
 * own a part has only its pages; at one address the parts are simply all there.
 *
 * `/` belongs to a different page in each part: the front page at the domain
 * itself (static, not this application's), the hub at `app.`, and at `admin.` and
 * `dev.` nothing of its own, so it leads to where the account's role starts.
 */

/**
 * `app`: what is published, read and taken by anybody: with an account, as a guest
 * (public lessons tried without one), or just looking. The server shows each only
 * what it may see.
 */
const PUBLISHED: RouteObject[] = [
  { path: '/', element: <HubPage /> },
  // Content lives at its address: /author/course/lesson (shared/lib/address.ts).
  // `:course` is "-" for a lesson that stands alone. Every other page starts
  // with a word no author's handle can be: the server keeps a list of them,
  // and tests/test_addresses.py holds that list to the paths in this file.
  { path: '/:author/:course', element: <CourseMapPage /> },
  {
    path: '/:author/:course/:lesson',
    lazy: async () => ({ Component: (await lessonPages()).LessonPage }),
  },
  {
    path: '/:author/:course/:lesson/test',
    lazy: async () => ({ Component: (await lessonPages()).LessonTestPage }),
  },
  {
    path: '/:author/:course/topics/:topic/check',
    lazy: async () => ({ Component: (await lessonPages()).TopicCheckPage }),
  },
  {
    path: '/glossary',
    lazy: async () => ({ Component: (await glossaryPage()).GlossaryPage }),
  },
  {
    path: '/help',
    lazy: async () => ({ Component: (await helpPages()).HelpIndexPage }),
  },
  {
    path: '/help/:slug',
    lazy: async () => ({ Component: (await helpPages()).HelpArticlePage }),
  },
];

/** `app`: what needs an account: a history, teachers or students, the studio. */
const ACCOUNT_LEARNING: RouteObject[] = [
  {
    // Only a student has teachers of their own.
    element: <RequireRole roles={STUDENTS} />,
    children: [
      {
        // Where an invitation link leads.
        path: '/join/:code',
        lazy: async () => ({ Component: (await teachingPages()).JoinPage }),
      },
    ],
  },
  {
    // What those who take lessons and tests have: a history of results.
    element: <RequireRole roles={LEARNERS} />,
    children: [
      {
        path: '/history',
        lazy: async () => ({ Component: (await historyPages()).HistoryPage }),
      },
      {
        path: '/history/:attemptId',
        lazy: async () => ({ Component: (await historyPages()).HistoryAttemptPage }),
      },
    ],
  },
  {
    // Refused by the server for anyone without the right to edit.
    path: '/help/new',
    handle: { wide: true },
    lazy: async () => ({ Component: (await helpPages()).HelpEditPage }),
  },
  {
    path: '/help/:slug/edit',
    handle: { wide: true },
    lazy: async () => ({ Component: (await helpPages()).HelpEditPage }),
  },
  {
    element: <RequireRole roles={['teacher']} />,
    children: [
      {
        path: '/teaching',
        lazy: async () => ({ Component: (await teachingPages()).TeachingPage }),
      },
      {
        path: '/teaching/inbox',
        lazy: async () => ({ Component: (await teachingPages()).InboxPage }),
      },
      {
        path: '/teaching/inbox/:submissionId',
        lazy: async () => ({ Component: (await teachingPages()).InboxResultPage }),
      },
      {
        path: '/studio',
        lazy: async () => ({ Component: (await studioPages()).StudioPage }),
      },
      {
        path: '/studio/new-lesson',
        lazy: async () => ({ Component: (await studioPages()).NewStandaloneLessonPage }),
      },
      {
        // A lesson that stands alone: "-" where its course would be.
        path: '/studio/:author/-/:lesson',
        lazy: async () => ({ Component: (await studioPages()).StandaloneLessonPage }),
      },
      {
        path: '/studio/:author/:course',
        // The editor needs room for the course tree beside the form.
        handle: { wide: true },
        lazy: async () => ({ Component: (await studioPages()).CourseLayout }),
        children: [
          {
            index: true,
            lazy: async () => ({ Component: (await studioPages()).CoursePage }),
          },
          {
            path: 'topics/:topic',
            lazy: async () => ({ Component: (await studioPages()).TopicPage }),
          },
          {
            path: 'new-topic',
            lazy: async () => ({ Component: (await studioPages()).NewTopicPage }),
          },
          {
            path: 'lessons/:lesson',
            lazy: async () => ({ Component: (await studioPages()).LessonPage }),
          },
          {
            path: 'new-lesson',
            lazy: async () => ({ Component: (await studioPages()).NewLessonPage }),
          },
        ],
      },
    ],
  },
];

/**
 * Learning is for everybody who has a part in the product, and for anybody without
 * an account; not for a developer, whose work is the installation itself.
 */
const LEARNING: RouteObject[] = [{ element: <RequireRole roles={MEMBERS} visitors />, children: PUBLISHED }];
const MEMBERS_ONLY: RouteObject[] = [{ element: <RequireRole roles={MEMBERS} />, children: ACCOUNT_LEARNING }];

/** The account's own page. It is wherever the account works: at every host but the front. */
const ACCOUNT: RouteObject[] = [
  {
    path: '/settings',
    lazy: async () => ({ Component: (await settingsPage()).SettingsPage }),
  },
];

/** `admin`: moderation, and the accounts of the installation. */
const MODERATION: RouteObject[] = [
  {
    element: <RequireRole roles={ACCOUNT_MANAGERS} permission="accounts.manage" />,
    children: [
      {
        path: '/access',
        lazy: async () => ({ Component: (await accessPage()).AccessPage }),
      },
    ],
  },
  {
    element: <RequireRole roles={SEO_MANAGERS} permission="seo.manage" />,
    children: [
      {
        path: '/seo',
        lazy: async () => ({ Component: (await seoPage()).SeoPage }),
      },
    ],
  },
  {
    element: <RequireRole roles={MODERATORS} />,
    children: [
      {
        path: '/moderation',
        lazy: async () => ({ Component: (await moderationPage()).ModerationPage }),
      },
      {
        path: '/feedback/inbox',
        lazy: async () => ({ Component: (await feedbackPages()).FeedbackInboxPage }),
      },
    ],
  },
];

/** At every host, for anybody: a message to the team, linked from the foot of every page. */
const FEEDBACK: RouteObject[] = [
  {
    path: '/feedback',
    lazy: async () => ({ Component: (await feedbackPages()).FeedbackPage }),
  },
];

/** `dev`: the way to what developers read. */
const TOOLS: RouteObject[] = [
  {
    element: <RequireRole roles={DEVELOPERS} />,
    children: [
      {
        path: '/portal',
        lazy: async () => ({ Component: (await portalPage()).PortalPage }),
      },
    ],
  },
];

/** `home`: the ways in. */
const ENTRANCE: RouteObject[] = [
  {
    element: <RequireAnonymous />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: <RegisterPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
    ],
  },
  // Opened from a mail, signed in or not.
  { path: '/verify-email', element: <VerifyEmailPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
];

/**
 * The front page is not a page of the application: the gateway serves the static
 * site there (frontend/site, a page per language). Asked for from inside the
 * application, it is loaded from the server.
 */
function FrontPage() {
  const { t } = useTranslation();
  // Loaded at `/` itself, the server gave this application instead of the static site
  // (a gateway without it, a developer's machine): loading again would do the same.
  const servedHere = OPENED_AT === '/';
  useEffect(() => {
    if (!servedHere) browser.leave('/');
  }, [servedHere]);
  if (!servedHere) return null;
  return (
    <nav className="flex flex-col items-start gap-3">
      <a href={urlOf('app', '/')} className="underline">
        {t('nav.hub')}
      </a>
      <Link to="/login" className="underline">
        {t('nav.signIn')}
      </Link>
    </nav>
  );
}

/** Where this page was first loaded, before the router moved anywhere. */
const OPENED_AT = globalThis.location?.pathname;

/** A way in asked for at a host that has none: it is at the front of the installation. */
function AtTheFront() {
  const { pathname, search } = useLocation();
  return <GoTo to={urlOf('home', `${pathname}${search}`)} />;
}

const ELSEWHERE: RouteObject[] = [
  '/login',
  '/register',
  '/verify-email',
  '/forgot-password',
  '/reset-password',
].map((path) => ({
  path,
  element: <AtTheFront />,
}));

const signedIn = (children: RouteObject[]): RouteObject => ({ element: <RequireAuth />, children });
/** A part with no page of its own at `/`: it leads to where the account's role starts. */
const START: RouteObject = { path: '/', element: <ToHome /> };

function pagesOf(part: Part | null): RouteObject[] {
  switch (part) {
    case null:
      // One address: everything, as one application. Its front page is the hub; the
      // static site is beside it, under a language (/en/).
      return [...LEARNING, ...FEEDBACK, signedIn([...MEMBERS_ONLY, ...ACCOUNT, ...MODERATION, ...TOOLS]), ...ENTRANCE];
    case 'home':
      return [{ path: '/', element: <FrontPage /> }, ...FEEDBACK, ...ENTRANCE];
    case 'app':
      return [...LEARNING, ...FEEDBACK, signedIn([...MEMBERS_ONLY, ...ACCOUNT]), ...ELSEWHERE];
    case 'admin':
      return [...FEEDBACK, signedIn([START, ...ACCOUNT, ...MODERATION]), ...ELSEWHERE];
    case 'dev':
      return [...FEEDBACK, signedIn([START, ...ACCOUNT, ...TOOLS]), ...ELSEWHERE];
  }
}

/** The routes of a part, inside the frame of the application. */
export function routesFor(part: Part | null): RouteObject[] {
  return [
    {
      element: <AppShell />,
      // If the frame itself fails there is nothing to keep around the message.
      errorElement: <RouteCrash />,
      children: [
        {
          // A page that fails is replaced by a message; the frame around it stays.
          errorElement: <RouteCrash />,
          children: [...pagesOf(part), { path: '*', element: <NotFoundPage /> }],
        },
      ],
    },
  ];
}

/** The router of the part this page was opened in. Made once the site is known (main.tsx). */
export const createRouter = () => createBrowserRouter(routesFor(partHere()));
